import { useState } from 'react';
import { IconButton } from '@mui/material';
import { CalendarMonth, Delete } from '@mui/icons-material';
import Box from '@mui/material/Box';
import { useDelete, useNotify } from 'react-admin';
import {
    AVATARS,
    INTERVENTION_CHANGE_STATUS_NODE_LIST_OBJECT,
    INTERVENTION_EXPORT_TAG,
    REALISEE_STATE_KEY,
} from '@/utils/CONST';
import DeleteDialog from '@/components/common/delete-dialog/delete-dialog';
import { Avatar } from '@/utils/types';
import { useParams } from 'react-router';
import extractIdFromURI from '@/utils/id';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import GetDocumentContainer from '@/components/common/documents-box/get-document-container';
import { ExportInterventionDialog } from '@/components/interventions/export-intervention/export-intervention';

interface Props {
    avatar: Avatar;
    withDelete?: boolean;
    withChangeDate?: boolean;
    onOpenPlannedDateDialog?: (interventionId: string) => void;
}

const InterventionActionField = ({ avatar, withDelete = false, withChangeDate = false, onOpenPlannedDateDialog }: Props) => {
    const [deleteOne, { isLoading }] = useDelete();
    const notify = useNotify();
    const { clientId, placeId } = useParams();

    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

    const goToAddPlannedDate = () => {
        if (onOpenPlannedDateDialog) {
            const interventionId = extractIdFromURI(avatar.id);
            if (interventionId) {
                onOpenPlannedDateDialog(interventionId);
            }
        }
    };

    const openExportDialog = () => {
        setIsExportDialogOpen(true);
    };

    const handleDeleteOne = () => {
        deleteOne(
            AVATARS,
            {
                id: avatar.id,
                meta: {
                    requestType: 'update',
                    id: `${avatar.id}/enable`,
                    data: {
                        enabled: false,
                        children: false,
                    },
                },
            },
            {
                onSuccess: () => {
                    notify('ra.notification.deleted', {
                        type: 'success',
                        messageArgs: { smart_count: 1 },
                    });
                    setOpenDeleteModal(false);
                },
                mutationMode: 'optimistic',
            },
        );
    };

    const handleDownload = (url: string) => {
        window.open(url, '_blank');
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'end',
                gap: 1,
            }}
        >
            <GetDocumentContainer tag={INTERVENTION_EXPORT_TAG}>
                {({ file }: any) => (
                    <Box
                        sx={{
                            display: 'flex',
                        }}
                    >
                        <>
                            {file && (
                                <IconButton
                                    color={'primary'}
                                    sx={{
                                        p: 0.5,
                                    }}
                                    onClick={() => handleDownload(file.url)}
                                >
                                    <DownloadIcon />
                                </IconButton>
                            )}

                            <IconButton
                                color={'primary'}
                                sx={{
                                    p: 0.5,
                                }}
                                onClick={openExportDialog}
                                disabled={!clientId || !placeId}
                            >
                                <PictureAsPdfIcon />
                            </IconButton>

                            {clientId && placeId && (
                                <ExportInterventionDialog
                                    open={isExportDialogOpen}
                                    onClose={() => setIsExportDialogOpen(false)}
                                    clientId={clientId}
                                    placeId={placeId}
                                    interventionId={extractIdFromURI(avatar.id) ?? ''}
                                />
                            )}
                        </>
                    </Box>
                )}
            </GetDocumentContainer>

            {withChangeDate && (
                <IconButton
                    disabled={
                        isLoading ||
                        INTERVENTION_CHANGE_STATUS_NODE_LIST_OBJECT[REALISEE_STATE_KEY].id ===
                            avatar.currentNode?.configuration?.idForNodeHumanReadable
                    }
                    onClick={goToAddPlannedDate}
                    color={'primary'}
                    sx={{
                        p: 0.5,
                    }}
                    data-cy={'addPlannedDate'}
                >
                    <CalendarMonth />
                </IconButton>
            )}

            {withDelete && (
                <IconButton
                    disabled={isLoading}
                    onClick={() => setOpenDeleteModal(true)}
                    color={'primary'}
                    sx={{
                        p: 0.5,
                    }}
                >
                    <Delete />
                </IconButton>
            )}

            {withDelete && openDeleteModal && (
                <DeleteDialog
                    isLoading={isLoading}
                    onClose={() => setOpenDeleteModal(false)}
                    onClick={handleDeleteOne}
                />
            )}
        </Box>
    );
};

export default InterventionActionField;
